import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
    IconArrowRight, ModalLayout,
    useWindowDimensions, ListItem, IconArrowLeft, IconSignPlus
} from '@Knowledge-OTP/znk-ui-components'
import { TabContent, TabPane } from 'reactstrap'
import ServicesFilter from '../Services'
import StatusesFilter from '../Statuses'
import StartDateFilter from '../StartDate'
import FinalizationDates from '../FinalizationDates'
import EducatorsFilter from '../Educators'
import PendingLessonsInner from '../PendingLessonsInner'
import CountriesFilter from '../Countries'
import useTranslation from '../../../../i18n/useTranslation'


const ModalFilterMobile = (props) => {
    const { 
        setQuery: setQueryProp = () => {}, query = {}, 
        resetFilters = () => {}, canReset = false 
    } = props
    const setQuery = useCallback(
        (newQuery) => {
            setQueryProp(newQuery)
            setOpen(false)
        }, [setQueryProp]
    ) 
    const [isOpen, setOpen] = useState(false)
    const toggle = () => setOpen(!isOpen)
    const { breakWidth } = useWindowDimensions()
    
    const {translate} = useTranslation()
    const Pages = {
        filters: {key: 'filters', name: 'Filters'},
        services: {key: 'services', name: translate('filters.plans.services.title')},
        statuses: {key: 'statuses', name: translate('filters.plans.status.title')},
        startDate: {key: 'start-date', name: translate('filters.plans.startDateTitle')},
        finalizationDate: {key: 'finalization-date', name: translate('filters.plans.finalizationDateTitle')},
        educators: {key: 'educators', name: translate('filters.plans.educatorsTitle')},
        pendingLessons: {key: 'pending-lessons', name: translate('filters.plans.pendingLessonsTitle')},
        countries: {key: 'countries', name: translate('filters.plans.countriesTitle')},
    }
    
    const [page, setPage] = useState(Pages.filters.key)
    useEffect(() => { if (!isOpen) setTimeout(() => setPage(Pages.filters.key), 1000) }, [isOpen, Pages])
    
    const modalTitle = useMemo(() => Object.values(Pages).find(({key}) => key === page)?.name, [page, Pages])
    const listItemClassName = 'w-100 hover-no-border py-2'
    return (
        <>
            <div className='m-0 p-0' onClick={toggle}>
                {props.children}
            </div>
            <ModalLayout
                className={`${breakWidth === 'SM' ? 'modal-botbar' : 'modal-centered'} modal-hauto modal-noborder-bottom`}
                disabledScrollAnimation={false}
                isOpen={isOpen}
                toggle={toggle}
                topHead={
                    <div className={`d-flex flex-row align-items-center`}>
                        {
                            Pages.filters.key !== page &&
                            <div className='hover-icon' onClick={() => setPage(Pages.filters.key)}>
                                <IconArrowLeft size={20} className={'text-disabled'} />
                            </div>
                        }
                        <span className={`h2 text-title m-0`}>{modalTitle}</span>
                    </div>
                }
            >
                <TabContent activeTab={page}>
                    <TabPane tabId={Pages.filters.key}>
                        <div className='m-0 p-0 d-flex flex-column'>
                            <ListItem
                                onClick={() => { setPage(Pages.services.key) }}
                                className={listItemClassName}
                                hasHover
                                Left={<span>{Pages.services.name}</span>}
                                Right={<div className='hover-icon'><IconArrowRight /></div>}
                                showBottomLine
                            />
                            <ListItem
                                onClick={() => { setPage(Pages.statuses.key) }}
                                className={listItemClassName}
                                hasHover
                                Left={<span>{Pages.statuses.name}</span>}
                                Right={<div className='hover-icon'><IconArrowRight /></div>}
                                showBottomLine
                            />
                            <ListItem
                                onClick={() => { setPage(Pages.startDate.key) }}
                                className={listItemClassName}
                                hasHover
                                Left={<span>{Pages.startDate.name}</span>}
                                Right={<div className='hover-icon'><IconArrowRight /></div>}
                                showBottomLine
                            />
                            <ListItem
                                onClick={() => { setPage(Pages.finalizationDate.key) }}
                                className={listItemClassName}
                                hasHover
                                Left={<span>{Pages.finalizationDate.name}</span>}
                                Right={<div className='hover-icon'><IconArrowRight /></div>}
                                showBottomLine
                            />
                            <ListItem
                                onClick={() => { setPage(Pages.educators.key) }}
                                className={listItemClassName}
                                hasHover
                                Left={<span>{Pages.educators.name}</span>}
                                Right={<div className='hover-icon'><IconArrowRight /></div>}
                                showBottomLine
                            />
                            <ListItem
                                onClick={() => { setPage(Pages.pendingLessons.key) }}
                                className={listItemClassName}
                                hasHover
                                Left={<span>{Pages.pendingLessons.name}</span>}
                                Right={<div className='hover-icon'><IconArrowRight /></div>}
                                showBottomLine
                            />
                            <ListItem
                                onClick={() => { setPage(Pages.countries.key) }}
                                className={listItemClassName}
                                hasHover
                                Left={<span>{Pages.countries.name}</span>}
                                Right={<div className='hover-icon'><IconArrowRight /></div>}
                                showBottomLine
                            />
                            {
                                canReset && (
                                    <ListItem
                                        onClick={resetFilters}
                                        className={listItemClassName}
                                        hasHover
                                        Left={<span className='text-error'>{translate('misc.cleanFilters')}</span>}
                                        Right={<div className='hover-icon'><IconSignPlus className={'rotate-90 text-error'} /></div>}
                                        showBottomLine
                                    />
                                )
                            }
                        </div>
                    </TabPane>
                    <TabPane tabId={Pages.services.key}>
                        <ServicesFilter
                            serviceFilters={query.serviceIds}
                            setServiceFilters={(newServices) => { setQuery({ ...query, serviceIds: newServices }) }}
                            useInner
                        />
                    </TabPane>
                    <TabPane tabId={Pages.statuses.key}>
                        <StatusesFilter 
                            useInner
                            statusFilters={query.status}
                            setStatusFilters={(newStatus) => { setQuery({ ...query, status: newStatus }) }}
                        />
                    </TabPane>
                    <TabPane tabId={Pages.startDate.key}>
                        <StartDateFilter
                            startDateFilter={query.startDateFilter}
                            setStartDateFilter={(start) => { setQuery({ ...query, startDateFilter: start }) }}
                            typeFilter='StartDate' />
                    </TabPane>
                    <TabPane tabId={Pages.finalizationDate.key}>
                        <FinalizationDates 
                            setEndDateFilter={(end) => { setQuery({ ...query, endDateFilter: end }) }}
                        />
                    </TabPane>
                    <TabPane tabId={Pages.educators.key}>
                        <EducatorsFilter
                            style={{maxWidth: 'auto'}}
                            className={`h45`}
                            educatorFilters={query.educatorUserIds}
                            setEducatorFilters={(educatorIds) => { setQuery({ ...query, educatorUserIds: educatorIds }) }}
                        />
                    </TabPane>
                    <TabPane tabId={Pages.pendingLessons.key}>
                        <PendingLessonsInner 
                            lessonsPending={query.lessonsPending}
                            setLessonsPending={(lp) => { setQuery({ ...query, lessonsPending: lp }) }}
                        />
                    </TabPane>
                    <TabPane tabId={Pages.countries.key}>
                        <CountriesFilter
                            style={{maxWidth: 'auto'}}
                            className={'h45'}
                            countryFilters={query.countryIds}
                            setCountryFilters={(ids) => {setQuery({ ...query, countryIds: ids })}}
                        />
                    </TabPane>
                </TabContent>
            </ModalLayout>
        </>
    )
}


export default ModalFilterMobile